import { da } from "date-fns/locale";
import React, { useState } from "react";
import { RxExternalLink } from "react-icons/rx";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HoverToDisplayFullTitle from "../../reusableComponents/HoverToDisplayFullTitle";
import {
  currentTimeInTimezone,
  isTimeLeft,
  parseAsDate,
  sortDateTime,
} from "../utils/utils";
import Timer from "../../reusableComponents/Timer";
import "../analytics/analytics.css";
import { customToast } from "../../reusableComponents/customToaster/customToast";
import { Tooltip, Typography } from "@mui/material";
import { useAuth } from "../../context/API_Client";
import MoreOptionsPopOver from "../../reusableComponents/moreOptionsPopOver/MoreOptionsPopOver";
import ToolTip from "../../reusableComponents/ToolTip";
import FileCopyIcon from "../../../assets/svg/fileCopy.svg";
import { colorBrandObject } from "../../../constants/brandings";
import { fileCopyDefaultColor } from "../../reusableComponents/constants";

const OneTimePaymentDetailSchema = (
  timeZone,
  settings,
  handleEditButton,
  handleDuplicateButton,
  handleDownloadQr,
  getPaymentDetails
) => {
  const { isAuthorizedPermission, resendSMSAndEmail, expirePaymentLink } =
    useAuth();

  const copyFunction = ({ value, paymentUrl }) => {
    const handleCopy = () => {
      navigator.clipboard.writeText(paymentUrl);
      customToast("success", "medium", `Copied`);
    };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginLeft: "-12px",
          minWidth: "100px",
          paddingRight: "30px",


        }}
      >
        <div>{value}</div>
        <div style={{ stroke: fileCopyDefaultColor }}>
          <FileCopyIcon stroke={`${colorBrandObject?.actionIconColor}`}
            style={{
              cursor: "pointer",
              position: "absolute",
              right: "0",
              marginRight: "16px",
              transform: "translateY(-50%)",
              top: "50%"
            }}
            onClick={handleCopy}
          />
        </div>
      </div>
    );
  };

  const formatDateWithoutSeconds = (timestamp) => {
    const originalString = timestamp;
    const modifiedString =
      originalString.slice(0, 16) + originalString.slice(19);

    return modifiedString;
  };

  const handleActionSelect = (data) => {
    if (data?.type === "sms" || data?.type === "email") {
      resendSMSAndEmail(
        data?.token,
        data?.type,
        (successData) => {
          customToast("success", "high", "Link sent successfully");
        },
        (errmsg) => {
          customToast("error", "high", errmsg);
        }
      );
    }
    if (data?.type === "edit") {
      handleEditButton(data);
    }
    if (data?.type === "duplicate") {
      handleDuplicateButton(data);
    }

    if (data?.type === "EXPIRE") {
      expirePaymentLink(
        data?.token,
        (successData) => {
          customToast("success", "high", "Link disabled successfully");
          getPaymentDetails(false)
        },
        (errmsg) => {
          customToast("error", "high", errmsg);
        }
      );
    }
    if (data?.type === "Download") {
      handleDownloadQr(data);
    }
  };

  const schema = {
    metadata: {
      title: "Payment Detials",
      minHeight: 600,
      position: "relative",
      rowHeight: 60,
      showTitle: false,
      defaultSortInfo: { name: "addedTimestamp", dir: -1 },
      defaultVisible: [
        "title",
        "businessUnitName",
        "token",
        "addedTimestamp",
        "amount",
        "status",
        "sessionExpiryTimestamp",
        "lastPaidAtTimestamp",
        "transactionId",
        "productName",
        "id"
      ]
    },
    fields: [
      {
        name: "title",
        header: "Title",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 180,
        defaultFlex: 1,
        textAlign: "center",
        sortable: false,
        render: ({ data }) => (
          <div
            style={{
              marginLeft: "-12px",
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: '1 1 0%'
            }}
          >
            {data?.title ? (
              <HoverToDisplayFullTitle title={data?.title} len={16} />
            ) : (
              <></>
            )}
          </div>
        ),
      },
      {
        name: "businessUnitName",
        header: "Business Units",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 160,
        textAlign: "center",
        sortable: false,
        defaultFlex: 1,
        render: ({ data }) => {
          return (
            <span
              style={{
                marginLeft: "-12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {data?.businessUnitName && (
                <HoverToDisplayFullTitle
                  title={data?.businessUnitName}
                  len={22}
                />
              )}
            </span>
          );
        },
      },
      {
        name: "token",
        header: "Payment Link Token",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 210,
        defaultFlex: 2,
        textAlign: "start",
        sortable: false,
        render: ({ data }) =>
          copyFunction({ value: data?.token, paymentUrl: data?.paymentUrl }),
      },
      {
        name: "addedTimestamp",
        header: "Created At",
        sort: sortDateTime,
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 230,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => (
          <span style={{ marginLeft: "-12px" }}>
            {formatDateWithoutSeconds(data.addedTimestamp)}
          </span>
        ),
      },
      {
        name: "amount",
        header: "Amount",
        visible: true,
        type: "number",
        filtering: false,
        minWidth: 140,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => (
          <span style={{ display: "flex", marginLeft: "-12px" }}>
            {data?.amountLocale}
          </span>
        ),
      },
      {
        name: "status",
        header: "Status",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 170,
        defaultFlex: 1,
        textAlign: "center",
        sortable: false,
        render: ({ data }) => {
          let backgroundColor;
          let color;

          if (data?.status === "PAID") {
            backgroundColor = "#D5FBD2";
            color = "#0B6A48";
          } else if (data?.status === "EXPIRED" || data?.status === "DISABLED") {
            backgroundColor = "#FDE3D6";
            color = "#B9232F";
          } else if (data?.status === "PENDING") {
            backgroundColor = "#FBEFCB";
            color = "#9E5707";
          } else if (data?.status === "FAILED") {
            backgroundColor = "#FDE3D6";
            color = "#B9232F";
          } else if (data?.status === "PROCESSING") {
            backgroundColor = "#FBEFCB";
            color = "#9E5707";
          }
          return (
            <span
              style={{
                marginLeft: "-20px",
                paddingBottom: "6px",
                paddingRight: "8px",
                paddingLeft: "8px",
                paddingTop: "5px",
                borderRadius: "8px",
                backgroundColor,
                color,
              }}
            >
              {data?.status}
            </span>
          );
        },
      },
      {
        name: "sessionExpiryTimestamp",
        header: "Expires On",
        visible: true,
        type: "number",
        minWidth: 230,
        defaultFlex: 1,
        textAlign: "center",
        justifyContent: "center",
        sortable: false,
        render: ({ data }) => (
          <span style={{ display: "flex", marginLeft: "-12px" }}>
            {data?.status === "PENDING" || data?.status === "FAILED" ? (
              data?.sessionExpiryTimestamp ? (
                formatDateWithoutSeconds(data.sessionExpiryTimestamp)
              ) : (
                <span>No Expiry</span>
              )
            ) : (
              <span> - </span>
            )}
          </span>
        ),
      },
      {
        name: "lastPaidAtTimestamp",
        header: "Paid At",
        visible: true,
        type: "string",
        filtering: false,
        sort: sortDateTime,
        minWidth: 230,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => (
          <span style={{ marginLeft: "-12px" }}>
            {data?.lastPaidAtTimestamp}
          </span>
        ),
      },
      {
        name: "transactionId",
        header: "Transaction ID",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 180,
        defaultFlex: 1,
        textAlign: "center",
        sortable: false,
        render: ({ data }) => {
          const transacId = data?.latestTransactionId;
          const canViewTransaction =
            isAuthorizedPermission("VIEW_ORDER_DETAILS");
          return (
            <span style={{ display: "flex", marginLeft: "-12px" }}>
              {transacId}
              {canViewTransaction ? (
                <Link
                  to={
                    "/transacs/payment_details/transaction_Details?transactionId=" +
                    transacId
                  }
                  target="_blank"
                >
                  {transacId && (
                    <RxExternalLink
                      style={{
                        width: "24px",
                        height: "16px",
                        cursor: "pointer",
                        position: "absolute",
                        right: "0",
                        marginRight: "16px",
                      }}
                    />
                  )}
                </Link>
              ) : (
                <Tooltip title={"Access denied contact Admin"} arrow>
                  <span
                    style={{
                      color: "gray",
                    }}
                  >
                    {transacId && (
                      <RxExternalLink
                        style={{
                          width: "24px",
                          height: "16px",
                          cursor: "pointer",
                          position: "absolute",
                          right: "0",
                          marginRight: "16px",
                        }}
                      ></RxExternalLink>
                    )}
                  </span>
                </Tooltip>
              )}
            </span>
          );
        },
      },
      {
        name: "productName",
        header: "Product Name",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 220,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => (
          <span
            style={{
              marginLeft: "-12px",
              paddingRight: "20px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {data?.productName && (
              <HoverToDisplayFullTitle title={data?.productName} len={20} />
            )}
          </span>
        ),
      },

      {
        name: "description",
        header: "Description",
        visible: false,
        type: "string",
        filtering: false,
        minWidth: 220,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => (
          <span
            style={{
              marginLeft: "-12px",
              paddingRight: "20px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {data?.description && (
              <HoverToDisplayFullTitle title={data?.description} len={20} />
            )}
          </span>
        ),
      },
      {
        name: "userId",
        header: "Created By",
        visible: false,
        type: "string",
        filtering: false,
        minWidth: 250,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => (
          <span
            style={{
              marginLeft: "-12px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "300px",
            }}
          >
            {data?.userId ? (
              <HoverToDisplayFullTitle title={data?.userId} len={30} />
            ) : (
              <></>
            )}
          </span>
        ),
      },

      {
        name: "firstName",
        header: "Customer Name",
        visible: false,
        type: "string",
        filtering: false,
        minWidth: 220,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => {
          const customerName =
            data?.shopper?.firstName && data?.shopper?.lastName
              ? `${data?.shopper?.firstName} ${data?.shopper?.lastName}`
              : data?.shopper?.firstName || data?.shopper?.lastName || "";

          return (
            <span
              style={{
                marginLeft: "-12px",
                paddingRight: "20px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {customerName && (
                <HoverToDisplayFullTitle title={customerName} len={20} />
              )}
            </span>
          );
        },
      },
      {
        name: "phoneNumber",
        header: "Phone Number",
        visible: false,
        type: "string",
        filtering: false,
        minWidth: 170,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => (
          <span style={{ marginLeft: "-12px" }}>
            {data?.shopper?.phoneNumber}
          </span>
        ),
      },
      {
        name: "emailAddress",
        header: "Email Address",
        visible: false,
        type: "string",
        filtering: false,
        minWidth: 220,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data }) => (
          <span
            style={{
              marginLeft: "-12px",
              paddingRight: "20px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {data?.shopper?.email && (
              <HoverToDisplayFullTitle title={data?.shopper?.email} len={20} />
            )}
          </span>
        ),
      },
      {
        name: "id",
        header: "Actions",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 100,
        defaultFlex: 1,
        textAlign: "center",
        sortable: false,
        render: ({ data }) => {
          let resendSmsCount =
            data?.resends?.find((val) => val?.medium === "SMS")?.count || 0;
          let resendEmailCount =
            data?.resends?.find((val) => val?.medium === "EMAIL")?.count || 0;

          let items = [];
          if (["PENDING", "FAILED"].includes(data?.status)) {
            items.push({
              id: "EDIT",
              label: (
                <Link to={"/paymentLinks/createPaymentlink"}>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <Typography>Edit</Typography>
                  </div>
                </Link>
              ),
              value: {
                phoneNumber: data?.shopper?.phoneNumber,
                token: data?.token,
                type: "edit",
                data: data,
              },
              enable: true,
            });
          }
          if (["PENDING", "FAILED", "EXPIRED", "DISABLED", "PAID"].includes(data?.status)) {
            items.push({
              id: "DUPLICATE",
              label: (
                <div style={{ display: "flex", gap: "4px" }}>
                  <Typography>Duplicate</Typography>
                </div>
              ),
              value: {
                phoneNumber: data?.shopper?.phoneNumber,
                token: data?.token,
                type: "duplicate",
                data: data,
              },
              enable: true,
            });
          }

          if (
            ["PENDING", "FAILED"].includes(data?.status) &&
            settings?.notifyViaSmsEnabled &&
            data?.context?.countryCode === "IN" &&
            data?.shopper?.phoneNumber?.includes("+91")
          ) {
            items.push({
              id: "SMS",
              label: (
                <div style={{ display: "flex", gap: "4px" }}>
                  <Typography>Resend via SMS</Typography>
                  {resendSmsCount >= 3 && (
                    <ToolTip
                      message={
                        "You can resend the payment link only 3 times. Please create a new link as limit has been reached."
                      }
                    />
                  )}
                </div>
              ),
              value: {
                phoneNumber: data?.shopper?.phoneNumber,
                token: data?.token,
                type: "sms",
              },
              enable: resendSmsCount < 3,
            });
          }

          if (
            ["PENDING", "FAILED"].includes(data?.status) &&
            data?.shopper?.email
          ) {
            items.push({
              id: "EMAIL",
              label: (
                <div style={{ display: "flex", gap: "4px" }}>
                  <Typography>Resend via Email</Typography>
                  {resendEmailCount >= 3 && (
                    <ToolTip
                      message={
                        "You can resend the payment link only 3 times. Please create a new link as limit has been reached."
                      }
                    />
                  )}
                </div>
              ),
              value: {
                email: data?.shopper?.email,
                token: data?.token,
                type: "email",
              },
              enable: resendEmailCount < 3,
            });
          }

          if (["PENDING", "FAILED"].includes(data?.status)) {
            items.push({
              id: "EXPIRE",
              label: (
                <div style={{ display: "flex", gap: "4px" }}>
                  <Typography>Disable Payment Link</Typography>
                </div>
              ),
              value: {
                token: data?.token,
                type: "EXPIRE",
              },
              enable: true,
            });
          }

          if (["PENDING", "FAILED"].includes(data?.status)) {
            items.push({
              id: "Download",
              label: (
                <div style={{ display: "flex", gap: "4px" }}>
                  <Typography>Download QR</Typography>
                </div>
              ),
              value: {
                phoneNumber: data?.shopper?.phoneNumber,
                token: data?.token,
                type: "Download",
                data: data,
              },
              enable: true,
            });
          }

          return (
            items?.length > 0 && (
              <MoreOptionsPopOver
                items={items}
                handleActionSelect={handleActionSelect}
              />
            )
          );
        },
      },

    ],
  };

  return schema;
};

export default OneTimePaymentDetailSchema;
